/*
 * Authors: mariushirjanu, Koen, Triadi
 * Created: 30/10/2015
 * Upgraded: 20/03/2023
 */
import _ from 'underscore';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { ICountryExtended, ILocationExtended, IState } from '@type_definitions/datatypes';
import { IInvoiceAddress, InvoiceAddressType, IShippingAddress, IVat, LocationDataType, OrderEntryAddressType, OrderEntryLocationDataService, ShippingAddressType } from '../order-entry-location-data.service';
import { Dictionary } from '@type_definitions/vendor';
import { OrderEntryLocationFormService } from '../order-entry-location-form.service';
import { IAddressData } from '../base-order-entry-location-to.service';
import { CountryDataService } from '@app/shared/location/country-data.service';
import { OrderEntryWebshopService } from '../../webshop/order-entry-webshop.service';
import { OrderEntryLocationService } from '../order-entry-location.service';
import { GaValidateService, IValidatingComponent } from '@app/shared/formly/ga-validate.service';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { OrderEntryInvoiceService } from '../invoice/order-entry-invoice.service';
import { OrderEntryShippingService } from '../shipping/order-entry-shipping.service';

export const LOCATION_ADDRESS_TEMPLATE:string = `
  <formly [form]="addressForm" [fields]="addressFields" [model]="addressModel" class="form-horizontal"></formly>
`;

@Component({
  template: LOCATION_ADDRESS_TEMPLATE
})
export class BaseLocationAddressComponent implements IValidatingComponent, OnInit, OnChanges {
  @Input() public addEditAllowed:boolean;
  @Input() public dataType:LocationDataType;
  @Input() public typeId:string;
  @Input() public fixedList:boolean;
  @Input() public addressType:InvoiceAddressType | ShippingAddressType;
  @Input() public locationToModel:OrderEntryShippingService|OrderEntryInvoiceService;
  @Input() public isAsyncLocationData:boolean;
  @Output() public onLocationChangeEmit:EventEmitter<ILocationExtended> = new EventEmitter<ILocationExtended>();

  public addressForm:UntypedFormGroup = new UntypedFormGroup({});
  public addressFields:IExtraFieldConfigurationObject[];
  public addressModel:IAddressData;
  protected loadedAddress:IInvoiceAddress | IShippingAddress; // contains the loaded address for the current address type
  public addEditModel:Partial<ILocationExtended>;
  private countryField:IExtraFieldConfigurationObject;
  private stateField:IExtraFieldConfigurationObject;
  private taxField:IExtraFieldConfigurationObject;
  protected onValidationReadyDeferred:IGADeferred<void>;

  constructor(
    protected countryDataService:CountryDataService,
    protected formlyService:GaFormlyService,
    protected orderEntryLocationService:OrderEntryLocationService,
    protected orderEntryLocationDataService:OrderEntryLocationDataService,
    protected orderEntryLocationFormService:OrderEntryLocationFormService,
    protected orderEntryWebshopService:OrderEntryWebshopService,
    protected validateService:GaValidateService,
    protected changeDetectorRef:ChangeDetectorRef,
    deferredService:GaDeferredService
  ) {
    this.onValidationReadyDeferred = deferredService.defer();
  }

  public ngOnInit():void {
    this.fillFields();
    
    if (!this.isAsyncLocationData) {
      this.onValidationReadyDeferred.resolve(); 
    }
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.locationToModel) {
      this.locationToModel = changes.locationToModel?.currentValue;
    }

    if (changes.addressType) {
      this.addressType = changes.addressType?.currentValue;
      this.loadedAddress = this.locationToModel.getLoadedAddress(this.addressType);
      this.addressModel = this.locationToModel?.selectedAddress;
      this.changeDetectorRef.detectChanges();
    }

    if (changes.addEditAllowed) {
      this.addEditAllowed = changes.addEditAllowed.currentValue;
      this.addressFields = this.createFields(); // recreate fields in case addEditAllowed is changed
    }
  }

  protected createFields():IExtraFieldConfigurationObject[] {
    if (!this.addEditAllowed) {
      return [
        this.orderEntryLocationFormService.createAddressValueField(this.addressModel)
      ];
    } else {
      this.countryField = this.orderEntryLocationFormService.createCountryField(this.onCountryChange);
      this.stateField = this.orderEntryLocationFormService.createStateField();
      this.taxField = this.orderEntryLocationFormService.createTaxField();

      let addressFields:IExtraFieldConfigurationObject[] = [
        this.orderEntryLocationFormService.createCompanyField(),
        this.dataType ===LocationDataType.SHIPPING? this.orderEntryLocationFormService.createOfficeField() : undefined,
        this.orderEntryLocationFormService.createAddressGroupField(),
        this.stateField,
        this.countryField,
        this.taxField
      ];

      if (this.orderEntryWebshopService.shopContext.shippingAllowDefaultAddress && (this.typeId !== OrderEntryAddressType.EXISTING_LOCATION || !this.addEditAllowed)) {
        addressFields.push(
          this.dataType ===LocationDataType.SHIPPING? this.orderEntryLocationFormService.createDefaultAddressField() : undefined
        );
      }

      //remove undefined
      addressFields = _.filter(<Dictionary<any>>addressFields, (af:boolean) => af);

      // manual trigger country field change to update state field
      if(this.addressModel?.country) {
        this.onCountryChange(this.addressModel.country);
      }

      return addressFields;
    }
  }

  protected fillFields():void {
    if(this.countryField) {
      this.formlyService.fillField(this.countryField, this.countryDataService.getAllCountries());
    }
  }

  private onCountryChange = (country?:ICountryExtended, oldCountry?:ICountryExtended):void => {
    this.showHideStateField(country, oldCountry, this.stateField, 'search');
    this.updateTax(country);
  };

  protected showHideStateField(country:ICountryExtended, oldCountry:ICountryExtended, field:IExtraFieldConfigurationObject, stateProp:string):void {
    if (country && oldCountry && country.id !== oldCountry.id) {
      this.addressModel[ stateProp ] = undefined;
      if (this.addressModel.location) {
        this.addressModel.location[ stateProp ] = undefined;
      }
    }

    if (!country || !country.hasState) {
      //hide and clear state when country does not have a state
      field.hide = true;
      field.templateOptions.options = [];
      field.formControl?.patchValue(undefined);
    } else {
      //load states
      field.hide = false;
      this.formlyService.fillField(field, this.orderEntryLocationDataService.states(country.id)
        .then((states:IState[]) => {
          if(field?.formControl && this.addressModel.location?.state){
            field.formControl.patchValue(this.addressModel.location.state);
          }
          return states;
        }));
    }
  }

  private updateTax(country:ICountryExtended):void {
    if (!country) {
      this.addressModel.vatName = undefined;
    } else {
      const selectedShopId:number = this.orderEntryWebshopService.selectedShop.id;
      const selectedCountryId:number = this.orderEntryWebshopService.selectedCountry.id;

      //load vat rules
      this.orderEntryLocationDataService.taxInfo(this.dataType, country.id, selectedShopId, selectedCountryId)
        .then((taxInfo:IVat) => {
          this.addressModel.vatName = taxInfo.name;
          if (this.taxField) {
            this.taxField.templateOptions.label = taxInfo.name;
            this.taxField.templateOptions.required = taxInfo.isMandatory;
            this.taxField.templateOptions.pattern = <any>(taxInfo.regex? new RegExp(taxInfo.regex) : /.*/);
            this.taxField.templateOptions.patternFormat = taxInfo.format;
          }
        });
    }
  }

  protected onLocationChange = (location:ILocationExtended, oldLocation?:ILocationExtended):void => {
    if (!_.isEqual(location, oldLocation)) {
      this.addEditModel = _.clone(location);
      this.orderEntryLocationService.updateLocationData(this.addressModel, this.addressForm, true);
      this.onLocationChangeEmit.emit(location);
    }
  };

  /**
   * @param selectedLocation {ILocationExtended}
   * @description this is model to be used only when tab 'shipping' or 'invoice' initialize
   * @protected
   */
  protected getInitialModel(selectedLocation:ILocationExtended):IAddressData {
    const model:IAddressData = <any>{
      location: selectedLocation
    };

    // this needs to be replaced with what stored in service model
    // because there are possibilities apart from location (selection)
    // input field might be changed manually by user
    if (this.addressModel?.company) {
      model.company = this.addressModel?.company;
    }

    if (this.addressModel?.office) {
      model.office = this.addressModel?.office;
    }

    if (this.addressModel?.address) {
      model.address = this.addressModel?.address;
    }

    if (this.addressModel?.zip) {
      model.zip = this.addressModel?.zip;
    }

    if (this.addressModel?.state) {
      model.state = this.addressModel?.state;
    }

    if (this.addressModel?.country) {
      model.country = this.addressModel?.country;
    }

    if (this.addressModel?.city) {
      model.city = this.addressModel?.city;
    }

    if (this.addressModel?.vatName) {
      model.vatName = this.addressModel?.vatName;
    }

    return model;
  }

  public beforeValidate = ():Promise<void> => {
    return this.onValidationReadyDeferred.promise;
  };
  
  public validate = ():Promise<void|string[]> => {
    return this.validateService.validateFields(this.addressFields, this.addressModel, true);
  };
}